import { Action, createReducer, on } from '@ngrx/store';
import * as Actions from './tasks.actions';
import { initialState, State } from './tasks.state';
import { taskAdapter } from './tasks.adapters';
import {
  RealTimeTaskListSort,
  RoomCleanTask,
  Task,
  TypeStatusCleanMapObject,
} from '@app/core/async-services/http/versioned/task-management/task-management.interface';
import cloneDeep from 'lodash.clonedeep';
import moment from 'moment';
import { Type } from '@app/core/models/user.interface';
import { Dictionary } from '@ngrx/entity';

const tasksReducer = createReducer(
  initialState,
  on(
    Actions.defaultTaskMenuDetermined,
    Actions.tasksMenuDoneButtonClicked,
    (state, { hotel, taskFilter }) => {
      const updatedState = cloneDeep(state);
      updatedState.tasksUI.menu.hotel = hotel;
      updatedState.tasksUI.menu.filter = taskFilter;
      updatedState.isNewTaskDataLoaded = false;
      updatedState.isTaskListLoaded = false;
      updatedState.newTaskData = undefined;
      return updatedState;
    },
  ),
  on(Actions.viewTaskPageEntered, (state) => ({
    ...state,
    isTaskDetailsLoaded: false,
  })),
  on(Actions.viewRoomCleanTaskPageInitialized, (state) => ({
    ...state,
    isTaskDetailsLoaded: false,
  })),
  on(Actions.getTaskDetailsSucceeded, (state, { payload }) => {
    return {
      ...state,
      assignees: payload.assignees,
      taskList: taskAdapter.updateOne(
        {
          id: payload.task.id,
          changes: payload.task,
        },
        state.taskList,
      ),
      isTaskDetailsLoaded: true,
    };
  }),
  on(Actions.pubNubTaskCreated, (state, { task }) => {
    const updatedState = cloneDeep(state);
    updatedState.taskList = taskAdapter.addOne(task as Task, state.taskList);
    return updatedState;
  }),
  on(Actions.pubNubTaskDeleted, (state, { task }) => {
    if (task.id !== undefined) {
      const updatedState = cloneDeep(state);
      updatedState.taskList = taskAdapter.removeOne(task.id, state.taskList);
      return updatedState;
    } else {
      return state;
    }
  }),
  on(Actions.pubNubTaskUpdated, (state, { task }) => {
    if (task.id !== undefined) {
      const updatedState = cloneDeep(state);
      updatedState.taskList = taskAdapter.updateOne(
        { id: task.id, changes: task },
        state.taskList,
      );
      return updatedState;
    } else {
      return state;
    }
  }),
  on(Actions.pubNubRoomCleanTaskCreated, (state, { task }) => {
    const updatedState = cloneDeep(state);
    updatedState.taskList = taskAdapter.addOne(task as Task, state.taskList);
    return updatedState;
  }),
  on(Actions.pubNubRoomCleanTaskDeleted, (state, { task }) => {
    if (task.id !== undefined) {
      const updatedState = cloneDeep(state);
      updatedState.taskList = taskAdapter.removeOne(task.id, state.taskList);
      return updatedState;
    } else {
      return state;
    }
  }),
  on(Actions.pubNubRoomCleanTaskUpdated, (state, { task }) => {
    if (task.id !== undefined) {
      const updatedState = cloneDeep(state);
      updatedState.taskList = taskAdapter.updateOne(
        { id: task.id, changes: task },
        state.taskList,
      );
      return updatedState;
    } else {
      return state;
    }
  }),
  on(Actions.getTaskListSucceeded, (state, { taskList }) => {
    const newTaskList = taskAdapter.upsertMany(taskList, state.taskList);
    return {
      ...state,
      taskList: newTaskList,
      isTaskListLoaded: true,
    };
  }),
  on(Actions.getNewTaskDataSucceeded, (state, { newTaskData }) => {
    return {
      ...state,
      newTaskData,
      isNewTaskDataLoaded: true,
    };
  }),
  on(Actions.homePageCreateTaskButtonClicked, (state) => ({
    ...state,
    assignees: state.newTaskData?.assignees,
  })),
  on(Actions.editAssigneeSucceeded, (state, { id, task }) => ({
    ...state,
    taskList: taskAdapter.updateOne(
      {
        id,
        changes: task,
      },
      state.taskList,
    ),
  })),
  on(Actions.createTaskSucceeded, (state, { task }) => ({
    ...state,
    taskList: taskAdapter.addOne(task, state.taskList),
  })),
  on(Actions.deleteTaskSucceeded, (state, { id }) => ({
    ...state,
    taskList: taskAdapter.removeOne(id, state.taskList),
  })),
  on(
    Actions.updateTaskStatusToOpenSucceeded,
    Actions.updateTaskStatusToInProgressSucceeded,
    Actions.updateTaskStatusToDoneSucceeded,
    (state, { id, updatedTaskStatusData }) => ({
      ...state,
      taskList: taskAdapter.updateOne(
        {
          id,
          changes: {
            modifiedByUserId: updatedTaskStatusData.modifiedByUserId,
            modifiedByUserType: updatedTaskStatusData.modifiedByUserType,
            modifiedOn: updatedTaskStatusData.modifiedOn,
            modifiedUserFullName: updatedTaskStatusData.modifiedUserFullName,
            statusId: updatedTaskStatusData.taskStatusId,
            statusName: updatedTaskStatusData.taskStatusName,
          },
        },
        state.taskList,
      ),
    }),
  ),
  on(Actions.getRoomCleanTaskListSucceeded, (state, { taskList, summary }) => {
    return {
      ...state,
      taskList: taskAdapter.upsertMany(taskList, state.taskList),
      roomCleanSummary: summary,
      isGetRoomCleanTaskListLoaded: true,
      isGetRoomCleanTaskListSuccessful: true,
    };
  }),
  on(Actions.getRoomCleanTaskListFailed, (state) => ({
    ...state,
    isGetRoomCleanTaskListLoaded: true,
    isGetRoomCleanTaskListSuccessful: false,
  })),
  on(
    Actions.defaultRealtimeRoomsMenuDetermined,
    Actions.realtimeRoomsMenuSubmitted,
    (state, { hotel }) => {
      const updatedState = cloneDeep(state);
      updatedState.realtimeRoomsUI.menu.hotel = cloneDeep(hotel);
      updatedState.isGetRoomCleanTaskListLoaded = false;
      updatedState.isGetRealtimeRoomsStatusLoaded = false;
      updatedState.newTaskData = undefined;
      updatedState.isNewTaskDataLoaded = false;
      return updatedState;
    },
  ),
  on(Actions.realtimeRoomsPortfolioHotelClicked, (state, { hotel }) => {
    const updatedState = cloneDeep(state);
    updatedState.realtimeRoomsUI.menu.hotel = hotel;
    updatedState.isGetRoomCleanTaskListLoaded = false;
    return updatedState;
  }),
  on(Actions.updateTaskStatusSucceeded, (state, { taskId, payload }) => ({
    ...state,
    taskList: taskAdapter.updateOne(
      {
        id: taskId,
        changes: {
          modifiedByUserId: payload.modifiedByUserId,
          modifiedByUserType: payload.modifiedByUserType,
          modifiedOn: payload.modifiedOn,
          modifiedUserFullName: payload.modifiedUserFullName,
          statusId: payload.taskStatusId,
          statusName: payload.taskStatusName,
        },
      },
      state.taskList,
    ),
  })),
  on(
    Actions.updatePMSStatusSucceeded,
    (state, { taskId, roomStatusId, currentUser }) => {
      const { details } = cloneDeep(
        state.taskList.entities[taskId],
      ) as RoomCleanTask;
      const { mpr, cleanTypeId } = state.newTaskData?.typeStatusCleanMap.find(
        (typeStatusCleanMap) =>
          typeStatusCleanMap.roomTypeId === details.roomTypeId &&
          typeStatusCleanMap.roomStatusId === roomStatusId,
      ) as TypeStatusCleanMapObject;
      details.roomStatusId = roomStatusId;
      details.targetMinutes = mpr;
      details.cleanTypeId = cleanTypeId;
      details.roomStatusName = state.newTaskData?.roomStatuses.find(
        (roomStatus) => roomStatus.roomStatusId === roomStatusId,
      )?.roomStatusName!;
      details.cleanTypeName = state.newTaskData?.cleanTypes.find(
        (cleanType) => cleanType.cleanTypeId === cleanTypeId,
      )?.name!;
      return {
        ...state,
        taskList: taskAdapter.updateOne(
          {
            id: taskId,
            changes: {
              details,
              modifiedOn: moment().toISOString(),
              modifiedByUserType: Type.MANAGER,
              modifiedUserFullName: `${currentUser.firstName} ${currentUser.lastName}`,
              modifiedByUserId: currentUser.id,
            },
          },
          state.taskList,
        ),
      };
    },
  ),
  on(
    Actions.updateChangeLinenSucceeded,
    (state, { taskId, changeLinen, currentUser }) => {
      const { details } = cloneDeep(
        state.taskList.entities[taskId],
      ) as RoomCleanTask;
      details.changeLinen = changeLinen;
      return {
        ...state,
        taskList: taskAdapter.updateOne(
          {
            id: taskId,
            changes: {
              details,
              modifiedOn: moment().toISOString(),
              modifiedByUserType: Type.MANAGER,
              modifiedUserFullName: `${currentUser.firstName} ${currentUser.lastName}`,
              modifiedByUserId: currentUser.id,
            },
          },
          state.taskList,
        ),
      };
    },
  ),
  on(Actions.updateRoomCleanTaskAssigneeSucceeded, (state, { task }) => ({
    ...state,
    taskList: taskAdapter.updateOne(
      { id: task.id, changes: task },
      state.taskList,
    ),
  })),
  on(Actions.fetchRealtimeRoomsStatusSucceeded, (state, { status }) => ({
    ...state,
    realtimeRoomsStatus: status,
    isGetRealtimeRoomsStatusLoaded: true,
    isGetRealtimeRoomsStatusSuccessful: true,
  })),
  on(Actions.fetchRealtimeRoomsStatusFailed, (state) => ({
    ...state,
    isGetRealtimeRoomsStatusLoaded: true,
    isGetRealtimeRoomsStatusSuccessful: false,
  })),
  on(Actions.pubNubAssigneeWorkingStatusUpdated, (state, { assignee }) => {
    const updatedState = cloneDeep(state);
    if (updatedState.realtimeRoomsStatus) {
      const updatedAssignee = updatedState.realtimeRoomsStatus.assignees.find(
        ({ assigneeId, assigneeType }) =>
          assigneeId === assignee.assigneeId &&
          assigneeType === assignee.assigneeType,
      );
      if (updatedAssignee) {
        updatedAssignee.workingStatusId = assignee.workingStatusId;
      }
    }
    return updatedState;
  }),
  on(Actions.createOrEditTaskNoteSucceeded, (state, { note, taskId }) => {
    if (taskId !== null) {
      const updatedState = cloneDeep(state);
      const task = updatedState.taskList.entities[taskId] as RoomCleanTask;
      const existingNote = task.notes?.find(
        (noteItem) => noteItem.id === note.id,
      );
      if (existingNote) {
        existingNote.text = note.text;
      } else if (Array.isArray(task.notes)) {
        task.notes.push(note);
      } else {
        task.notes = [note];
      }
      return {
        ...state,
        taskList: taskAdapter.updateOne(
          { id: taskId, changes: task },
          state.taskList,
        ),
      };
    } else {
      return state;
    }
  }),
  on(Actions.roomCleanTaskNoteViewed, (state, { id }) => {
    const updatedState = cloneDeep(state);
    const tasks = updatedState.taskList.entities as Dictionary<RoomCleanTask>;
    tasks[id]?.notes?.forEach(
      (note) => (note.taskNoteRead = moment().toISOString()),
    );
    return updatedState;
  }),
  on(Actions.realtimeRoomsViewChanged, (state, { view }) => ({
    ...state,
    realtimeRoomsView: view,
  })),
  on(Actions.realtimeRoomsListViewSortChanged, (state, { column }) => {
    const newSort: RealTimeTaskListSort = { column, orderBy: null };
    const currentSort = state?.realtimeRoomsTaskListSort;
    if (currentSort?.column === null && currentSort.orderBy === null) {
      newSort.orderBy = 'asc';
    } else if (column === currentSort?.column) {
      newSort.orderBy = currentSort.orderBy === 'asc' ? 'desc' : 'asc';
    } else if (column !== currentSort?.column) {
      newSort.orderBy = 'asc';
    }
    return {
      ...state,
      realtimeRoomsTaskListSort: newSort,
    };
  }),
);

export function reducer(state: State | undefined, action: Action) {
  return tasksReducer(state, action);
}
